// src/pages/MRPInventoryPivot.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import UserAccessSummary from "../components/layout/UserAccessSummary";
import { getCurrentUser } from "../utils/auth";
import { usePermissions } from "../utils/permissions";
import {
  buildInventoryPivotQuery,
  parseInventoryPivotFilters,
  INVENTORY_PIVOT_EMPTY_TOKEN,
} from "../utils/mrpInventoryPivot";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const DATA_ENDPOINT = "/api/mrp/inventory/pivot";
const EXPORT_ENDPOINT = "/api/mrp/inventory/pivot/export";

const PIVOT_ROW_LAYOUT = [
  { type: "value", label: "Cust Code", valueField: "custCode", valueClass: "text-emerald-600 font-semibold" },
  { type: "value", label: "Warehouse", valueField: "warehouse", valueClass: "text-blue-600 font-semibold" },
  { type: "header", label: "Retail SKUs", field: "retailSku" },
  { type: "header", label: "Product Code", field: "productCode", bold: true },
  { type: "metric", label: "Warehouse Stocks", field: "warehouseStocks" },
  { type: "metric", label: "On Water", field: "onWater" },
  { type: "metric", label: "Open Orders", field: "walmartOpenOrders" },
  { type: "metric", label: "Planning", field: "planning" },
  { type: "spacer" },
  { type: "metric", label: "Available (incl. On Water)", field: "availableInclOnWater", highlight: true },
];

export default function MRPInventoryPivot() {
  const location = useLocation();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const me = getCurrentUser();
  const { getLevel } = usePermissions();
  const permissionLevel = getLevel(me?.role, "mrp-inventory");

  const filtersFromSearch = useMemo(
    () => parseInventoryPivotFilters(location.search || ""),
    [location.search]
  );
  const filterQuery = useMemo(
    () => buildInventoryPivotQuery(filtersFromSearch),
    [filtersFromSearch]
  );

  const filterSummary = useMemo(() => {
    const entries = Object.entries(filtersFromSearch || {}).filter(([, values]) => Array.isArray(values) && values.length);
    if (!entries.length) return "No filters applied";
    return entries
      .map(([key, values]) => {
        const label = key.replace(/([A-Z])/g, " $1").trim();
        const formatted = values
          .map((value) => (value === INVENTORY_PIVOT_EMPTY_TOKEN ? "(Blank)" : value))
          .join(", ");
        return `${label}: ${formatted}`;
      })
      .join(" | ");
  }, [filtersFromSearch]);

  const fetchPivot = useCallback(async () => {
    if (permissionLevel === "None") {
      setGroups([]);
      setLoading(false);
      setError("");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const headers = {};
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";
      const response = await fetch(`${API_BASE}${DATA_ENDPOINT}${filterQuery}`, { headers });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to load MRP Inventory Pivot data.");
      }
      const payload = await response.json().catch(() => ({}));
      if (payload?.ok !== true) {
        throw new Error(payload?.error || "Failed to load MRP Inventory Pivot data.");
      }
      const blocks = Array.isArray(payload.groups) ? payload.groups : [];
      setGroups(blocks);
    } catch (err) {
      setGroups([]);
      setError(err?.message || "Failed to load MRP Inventory Pivot data.");
    } finally {
      setLoading(false);
    }
  }, [permissionLevel, me?.email, me?.role, filterQuery]);

  useEffect(() => {
    fetchPivot();
  }, [fetchPivot]);

  const exportDisabled = groups.length === 0;

  const onExport = useCallback(async () => {
    if (exportDisabled) return;
    setExporting(true);
    setError("");
    try {
      const headers = {};
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";
      const response = await fetch(`${API_BASE}${EXPORT_ENDPOINT}${filterQuery}`, { headers });
      if (!response.ok) {
        let message = "Unable to export pivot.";
        try {
          const payload = await response.json();
          if (payload?.message) message = payload.message;
          else if (payload?.error === "no_data") message = "No data available for export.";
          else if (payload?.error) message = payload.error;
        } catch (err) {
          // ignore parse errors
        }
        throw new Error(message);
      }
      const blob = await response.blob();
      const disposition = response.headers.get("Content-Disposition") || "";
      const suggested = filenameFromDisposition(disposition);
      const fallback = `MRP_Inventory_Pivot_${formatTimestampForFilename(new Date())}.xlsx`;
      const filename = suggested || fallback;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
    } catch (err) {
      setError(err?.message || "Unable to export pivot.");
    } finally {
      setExporting(false);
    }
  }, [exportDisabled, me?.email, me?.role, filterQuery]);

  const onBack = useCallback(() => {
    navigate("/mrp/inventory");
  }, [navigate]);

  if (permissionLevel === "None") {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-slate-900">MRP Inventory Pivot</h1>
          <UserAccessSummary />
        </div>
        <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          You do not have access to MRP Inventory.
        </div>
        <button
          type="button"
          onClick={onBack}
          className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
        >
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">MRP Inventory Pivot</h1>
          <p className="text-sm text-slate-600">Cust Code -> Warehouse blocks derived from current MRP Inventory filters.</p>
        </div>
        <UserAccessSummary />
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
        <button
          type="button"
          onClick={onBack}
          className="rounded border border-slate-300 bg-white px-3 py-2 font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        >
          Back
        </button>
        <button
          type="button"
          onClick={fetchPivot}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-2 font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-40"
        >
          <svg
            className={`h-4 w-4 ${loading ? "animate-spin text-blue-500" : "text-slate-500"}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4 10a6 6 0 0110.39-4.19l1.42-1.42A8 8 0 102 10h2zm12-4h-4v2h4V6z"
              clipRule="evenodd"
            />
          </svg>
          Refresh
        </button>
        <button
          type="button"
          onClick={onExport}
          disabled={exportDisabled || exporting}
          className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-2 font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <svg className="h-4 w-4 text-slate-500" viewBox="0 0 20 20" fill="currentColor">
            <path d="M13 7V3H7v4H4l6 6 6-6h-3z" />
            <path d="M4 15h12v2H4z" />
          </svg>
          {exporting ? "Exporting..." : "Export Pivot"}
        </button>
        <span className="text-xs text-slate-500">{filterSummary}</span>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <div className="rounded border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500 shadow-sm">
          Loading pivot data...
        </div>
      ) : groups.length === 0 ? (
        <div className="rounded border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500 shadow-sm">
          No pivot data available for the current filters.
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-slate-500">
            Row layout per block: Cust Code -> Warehouse -> Retail SKUs -> Product Code -> Warehouse Stocks -> On Water -> Open Orders -> Planning -> spacer -> Available (incl. On Water).
          </p>
          {groups.map((group) => (
            <PivotBlock key={`${group.custCode || ""}||${group.warehouse || ""}`} group={group} />
          ))}
        </div>
      )}
    </div>
  );
}

function PivotBlock({ group }) {
  const items = Array.isArray(group?.items) ? group.items : [];
  const displayCust = group?.custCode || "(Blank)";
  const displayWarehouse = group?.warehouse ? group.warehouse : "(Blank)";

  const renderCells = (rowConfig) => {
    if (rowConfig.type === "header") {
      return items.map((item, idx) => (
        <td
          key={`${group.custCode || "blank"}-${group.warehouse || "blank"}-${rowConfig.field}-${idx}`}
          className={`border-b px-3 py-2 text-center text-sm ${rowConfig.bold ? "font-semibold text-slate-800" : "text-slate-700"}`}
          style={{ minWidth: 110 }}
        >
          {item?.[rowConfig.field] || ""}
        </td>
      ));
    }
    if (rowConfig.type === "metric") {
      return items.map((item, idx) => (
        <td
          key={`${group.custCode || "blank"}-${group.warehouse || "blank"}-${rowConfig.field}-${idx}`}
          className={`border-b px-3 py-2 text-center tabular-nums text-sm ${
            rowConfig.highlight ? "font-semibold text-emerald-700" : "text-slate-800"
          }`}
          style={{ minWidth: 110 }}
        >
          {renderPivotValue(item?.[rowConfig.field])}
        </td>
      ));
    }
    return items.map((_, idx) => (
      <td
        key={`${group.custCode || "blank"}-${group.warehouse || "blank"}-placeholder-${idx}`}
        className="border-b px-3 py-2"
        style={{ minWidth: 110 }}
      />
    ));
  };

  return (
    <div className="space-y-2 rounded border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-2 text-sm text-slate-600">
        <div className="font-semibold text-slate-800">
          Cust {displayCust} · Warehouse {displayWarehouse}
        </div>
        <div>{items.length.toLocaleString()} SKU columns</div>
      </div>
      <div className="overflow-auto">
        <table className="min-w-full border-collapse text-sm">
          <tbody>
            {PIVOT_ROW_LAYOUT.map((rowConfig, idx) => {
              if (rowConfig.type === "spacer") {
                return (
                  <tr key={`${group.custCode || "blank"}-${group.warehouse || "blank"}-spacer-${idx}`}>
                    <td colSpan={items.length + 2} className="py-1" />
                  </tr>
                );
              }
              if (rowConfig.type === "value") {
                const value =
                  rowConfig.valueField === "custCode"
                    ? displayCust
                    : rowConfig.valueField === "warehouse"
                    ? displayWarehouse
                    : "";
                const valueClass = rowConfig.valueClass || "text-slate-800";
                return (
                  <tr key={`${group.custCode || "blank"}-${group.warehouse || "blank"}-value-${rowConfig.valueField}`}>
                    <th className="w-44 border-b bg-white px-3 py-2 text-left text-sm font-semibold text-slate-700">
                      {rowConfig.label}
                    </th>
                    <td className={`w-36 border-b px-3 py-2 text-left text-sm ${valueClass}`}>
                      {value || "(Blank)"}
                    </td>
                    {renderCells(rowConfig)}
                  </tr>
                );
              }
              return (
                <tr
                  key={`${group.custCode || "blank"}-${group.warehouse || "blank"}-${rowConfig.field || rowConfig.label}-${idx}`}
                  className={rowConfig.highlight ? "bg-amber-50" : ""}
                >
                  <th
                    className={`w-44 border-b px-3 py-2 text-left text-sm font-semibold ${
                      rowConfig.highlight ? "text-emerald-700" : "text-slate-700"
                    }`}
                  >
                    {rowConfig.label}
                  </th>
                  <td className="w-36 border-b px-3 py-2" />
                  {renderCells(rowConfig)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function renderPivotValue(value) {
  if (value === null || value === undefined) return "";
  const numberValue = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numberValue)) return "";
  return numberValue === 0 ? "0" : numberValue.toLocaleString();
}

function filenameFromDisposition(value = "") {
  const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(value);
  if (match && match[1]) {
    try {
      return decodeURIComponent(match[1]);
    } catch (err) {
      return match[1];
    }
  }
  return "";
}

function formatTimestampForFilename(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(
    date.getMinutes()
  )}${pad(date.getSeconds())}`;
}

